import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { useAddLink, useGetFolderList } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';

const createLinkKbFormSchema = (t: (key: string) => string) =>
  z.object({
    link: z.string().trim().url({ message: t('MUST_BE_VALID_URL') }),
    allow_recursive: z.boolean(),
  });

type LinkKbFormValues = z.infer<ReturnType<typeof createLinkKbFormSchema>>;

const linkKbFormDefaultValues: LinkKbFormValues = {
  link: '',
  allow_recursive: false,
};

type AddKbLinkModalProps = {
  folderId: string;
  projectKey: string;
};

export function AddKbLinkModal({ folderId, projectKey }: AddKbLinkModalProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const linkKbFormSchema = createLinkKbFormSchema(t);
  const form = useForm<LinkKbFormValues>({
    defaultValues: linkKbFormDefaultValues,
    resolver: zodResolver(linkKbFormSchema),
    mode: 'onChange',
  });

  const { isDirty, isValid } = form.formState;
  const { mutateAsync: addLinkMutate, isPending } = useAddLink();
  const { data: folderListData } = useGetFolderList(projectKey, !!projectKey);
  const folder = useMemo(
    () => folderListData?.data?.folders?.find((f) => f._id === folderId),
    [folderId, folderListData?.data?.folders]
  );
  const canEdit = useIsOwner(folder);

  const resetState = () => {
    form.reset(linkKbFormDefaultValues);
  };

  const submitHandler = async (values: LinkKbFormValues) => {
    if (!canEdit) return;

    try {
      const response = await addLinkMutate({
        link: values.link,
        project_key: projectKey,
        folder_id: folderId,
        allow_recursive: values.allow_recursive,
      });

      if (!response.is_success) {
        throw new Error(response.detail || t('FAILED_TO_ADD_LINK_TO_KB'));
      }

      toast({
        variant: 'success',
        title: t('LINK_ADDED'),
        description: t('URL_QUEUED_FOR_KB_PROCESSING'),
      });
      resetState();
      setOpen(false);
    } catch (error) {
      toast({
        title: t('UNABLE_TO_ADD_LINK'),
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen && !canEdit) return;
        setOpen(nextOpen);
        if (!nextOpen) {
          resetState();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!canEdit}>
          <Plus className="h-4 w-4" />
          {t('ADD_LINK')}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-6">
        <DialogHeader>
          <DialogTitle>{t('ADD_LINK')}</DialogTitle>
          <DialogDescription>{t('PROVIDE_URL_FOR_KNOWLEDGE_FOLDER')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(submitHandler)} className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="link"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('LINK')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('LINK_URL_PLACEHOLDER')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="allow_recursive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('RECURSIVE_CRAWL')}</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {t('FOLLOW_ELIGIBLE_CHILD_PAGES')}
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" size="sm" className="w-20">
                  {t('CANCEL')}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                size="sm"
                className="min-w-20"
                disabled={isPending || !isValid || !isDirty}
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('ADDING')}
                  </>
                ) : (
                  t('ADD')
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
